import { find, findIndex, get, toNumber } from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  InspectionContext,
  isInspectionSigned,
} from '../../contexts/InspectionContext';
import AppDivisionContext from '../../contexts/AppDivisionContext';
import { getDownloadUrl } from '../../utils/api/upload';
import uploadApi from '../../utils/api/upload';
import MapEditor from '../MapEditor';
import { MapLoadingError } from '../MapEditor/MapLoadingError';
import { MapLoadingSpinner } from '../MapEditor/MapLoadingSpinner';
import { useDocType } from '../Projects/useDocType';
import { useInspectionUploads } from '../Projects/useInspectionUploads';
import { useSiteMaps } from '../Projects/useSiteMaps';
import {
  ProjectPermissionContextProvider,
  useProjectPermission,
} from '../../contexts/ProjectPermissionContext';
import { useProject } from '../../hooks/project';
import clientApi from '../../utils/api/client';
import { findingApi } from '../../utils/api/finding';
import { useProjectInspectionsSlim } from '../../hooks/inspection';
import { withProjectContext } from '../../hoc/withProjectContext';
import { useMediaQuery } from "react-responsive"
import TenantFeatureContext from '../../contexts/TenantContext';
import { useOffline } from '../../contexts/OfflineContext';
import { useInspectionTemplateValidation } from "../../hooks/inspectionTemplateValidation"

export const MapEditorRouteContext = React.createContext();

/**
 * extract data from route, for map editor
 * project, map, notes, inspection findings, control measures
 *
 * @todo inspection findings dataSource
 */
function MapEditorRoute({ history, match, location }) {
  const { projectId, mapId } = match.params;
  const inspectionContext = useContext(InspectionContext);
  const featureContext = useContext(TenantFeatureContext)
  const appDivisionContext = useContext(AppDivisionContext);
  const projectPermissionContext = useProjectPermission(false);
  const isTabletOrLess = useMediaQuery({ maxWidth: 768 })
  const [mapMobileView, setMapMobileView] = useState(isTabletOrLess ? true : false)
  const [showCalibrateMap, setShowCalibrateMap] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const isOffline = useOffline();
  const inspectionTemplateData = useInspectionTemplateValidation(projectId, mapId)
  const templateId = inspectionTemplateData.data?.inspection_template_type_id

  const mapImgSrc = useRef(getDownloadUrl(mapId));

  /** @todo initial state from optional route props */
  const [selectedInspectionIndex, setSelectedInspectionIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [client, setClient] = useState(null);
  const [secondaryTemplateUploadId, setSecondaryTemplateUploadId] = useState(null);

  const projectQuery = useProject(projectId, setError);

  // Get project_doc_type_id directly from inspectionTemplateData
  const projectDocTypeId = inspectionTemplateData.data?.doc_type_id || null;

  const maps = useSiteMaps(
    projectId,
    projectDocTypeId,
    setError
  );

  // Slim list (no findings). Findings are fetched per-selection below.
  // Do not paginate — neighbour-id math at the dataSources block depends on
  // the full ordered list.
  const inspectionsQuery = useProjectInspectionsSlim(projectId, setError);
  const inspections = useMemo(() => {
    if (!inspectionsQuery?.data || !inspectionsQuery.data.length) {
      return [];
    }

    if (!templateId) {
      return inspectionsQuery.data;
    }

    return inspectionsQuery.data.filter(
      (insp) => insp?.inspection_template?.inspectionTemplateType?.id === templateId
    );
  }, [inspectionsQuery.data, templateId])

  const inspectionUploads = useInspectionUploads(
    projectId,
    selectedInspectionIndex !== null && inspections.length > 0
      ? inspections[selectedInspectionIndex]?.id
      : null,
    setError,
  );

  const [inspectId, setInspectId] = useState([])

  useEffect(() => {
    setInspectId((prevId) => [...prevId, inspectionContext.inspectionId])
  }, [inspectionContext.inspectionId])

  useEffect(() => {
    if (!inspectionsQuery.isFetched) return;

    if (!inspections || inspections.length === 0) {
      setSelectedInspectionIndex(null);

      return;
    }

    const i = location.state
      ? findIndex(inspections, {
        id: toNumber(location.state.inspectionId),
      })
      : -1;

    setSelectedInspectionIndex(i > -1 ? i : 0);
  }, [inspectionsQuery.isFetched, inspections, location.state]);

  const findingsRequestRef = useRef(null);

  useEffect(() => {
    if (selectedInspectionIndex === null) {
      inspectionContext.setInspectionLoaded(false);
      inspectionContext.resetInspection();
      findingsRequestRef.current = null;

      return;
    }
    const selected = inspections[selectedInspectionIndex];

    if (!selected) return;

    const requestId = selected.id;
    findingsRequestRef.current = requestId;

    findingApi.index(requestId)
      .then((findings) => {
        if (findingsRequestRef.current !== requestId) return;
        inspectionContext.updateInspection({
          ...selected,
          findings: findings || [],
        });
      })
      .catch((err) => {
        if (findingsRequestRef.current !== requestId) return;
        setError(err);
      });
  }, [selectedInspectionIndex, inspections]);

  /** Keep selectedInspectionIndex in range when inspections change */
  useEffect(() => {
    if (!inspections || inspections.length === 0) {
      if (selectedInspectionIndex !== null) setSelectedInspectionIndex(null);

      return;
    }
    if (
      selectedInspectionIndex === null ||
      selectedInspectionIndex < 0 ||
      selectedInspectionIndex >= inspections.length
    ) {
      setSelectedInspectionIndex(0);
    }
  }, [inspections, selectedInspectionIndex]);

  /** Required variables before loading is complete */
  useEffect(() => {
    if (maps && projectQuery.data && inspections && inspectionTemplateData.data) {
      setLoading(false);
    }
  }, [maps, projectQuery.data, inspections, inspectionTemplateData.data]);

  useEffect(() => {
    if (projectQuery.data && projectQuery.data.archivedAt) {
      projectPermissionContext.setReadOnly(true);
    }

    if (projectQuery.data && projectQuery.data.client_id) {
      clientApi
        .show(projectQuery.data.client_id)
        .then((data) => setClient(data));
    }
  }, [projectQuery.data]);

  // Compute projectUpload early for use in effects
  const projectUpload = useMemo(() => {
    return find(maps, {
      upload: {
        GUID: mapId,
      },
    });
  }, [maps, mapId]);

  // Fetch project-inspection uploads for secondary templates to get upload ID
  useEffect(() => {
    const fetchSecondaryTemplateUploadId = async () => {
      if (
        // inspectionTemplateData.data?.is_primary === false &&
        selectedInspectionIndex !== null &&
        inspections.length > 0 &&
        projectId &&
        projectUpload
      ) {
        try {
          const selectedInspection = inspections[selectedInspectionIndex];

          const uploads = await uploadApi.projectInspection.index(
            projectId,
            selectedInspection.id,
            projectDocTypeId
          );

          let matchingUpload = uploads.find(
            (upload) => upload.project_upload_id === projectUpload.id
          );

          if (!matchingUpload && uploads.length > 0) {
            matchingUpload = uploads.find(
              (upload) => upload.upload?.GUID === mapId || upload.project_upload?.upload?.GUID === mapId
            );
          }

          if (!matchingUpload && uploads.length === 1) {
            matchingUpload = uploads[0];
          }

          if (matchingUpload) {
            setSecondaryTemplateUploadId(matchingUpload.id);
          } else {
            setSecondaryTemplateUploadId(null);
          }
        } catch (err) {
          setSecondaryTemplateUploadId(null);
        }
      } else {
        setSecondaryTemplateUploadId(null);
      }
    };

    fetchSecondaryTemplateUploadId();
  }, [
    inspectionTemplateData.data?.is_primary,
    selectedInspectionIndex,
    inspections,
    projectId,
    projectUpload,
    projectDocTypeId,
    mapId
  ]);

  //

  if (error) {
    return <MapLoadingError projectId={Number(projectId)} content={error} />;
  }

  if (loading === true || !client) {
    return <MapLoadingSpinner />;
  }

  if (!projectUpload) {
    setError('Missing required variables: projectUpload');

    return null;
  }

  if (
    !appDivisionContext.appDivisionId
  ) {
    setError('Missing required variables: divisionId');

    return null;
  }
  /**
   * Define data sources
   *
   * @important {positionableType} must be unique!
   *
   * @see {PositionableDataSource}
   */

  const inspectionUpload = find(inspectionUploads || [], {
    project_upload_id: projectUpload.id,
  });

  const dataSources = [];

  // Only add control_measure, pollutant, and commentables if using primary template
  if (inspectionTemplateData.data?.is_primary === true) {
    dataSources.push(
      {
        positionableType: 'control_measure',
        positionableContext: 'legend_items',
        targetType: 'project_uploads',
        targetId: projectUpload.id,
        order: 2,
        positionableIndexArguments: [projectQuery.data.id, appDivisionContext.appDivisionId],
        sourceModelFormModalProps: {
          isSmall: false,
        },
        sourceModelFormProps: {
          readOnly: projectPermissionContext.readOnly,
        },
        enableSearch: true,
        startInspectionId: inspections[selectedInspectionIndex]
          ? inspections[selectedInspectionIndex].id
          : -1,
        endInspectionId: inspections[selectedInspectionIndex + 1]
          ? inspections[selectedInspectionIndex + 1].id
          : null,
      },
      {
        positionableType: 'pollutant',
        positionableContext: 'legend_items',
        targetType: 'project_uploads',
        targetId: projectUpload.id,
        order: 3,
        positionableIndexArguments: [projectQuery.data.id, appDivisionContext.appDivisionId],
        sourceModelFormModalProps: {
          isSmall: false,
        },
        sourceModelFormProps: {
          readOnly: projectPermissionContext.readOnly,
        },
        enableSearch: true,
        startInspectionId: inspections[selectedInspectionIndex]
          ? inspections[selectedInspectionIndex].id
          : -1,
        endInspectionId: inspections[selectedInspectionIndex + 1]
          ? inspections[selectedInspectionIndex + 1].id
          : null,
      },
      {
        positionableType: 'commentables',
        positionableTypeLabel: 'Notes',
        targetType: 'project_uploads',
        targetId: projectUpload.id,
        positionableIndexArguments: ['project_uploads', projectUpload.id],
        order: 4,
        maxInstances: 1,
        sourceModelFormProps: {
          readOnly: projectPermissionContext.readOnly,
        },
        startInspectionId: inspections[selectedInspectionIndex]
          ? inspections[selectedInspectionIndex].id
          : -1,
        endInspectionId: inspections[selectedInspectionIndex + 1]
          ? inspections[selectedInspectionIndex + 1].id
          : null,
      },
    );
  }

  if (inspectionTemplateData.data?.is_primary === false && inspections.length && selectedInspectionIndex !== null) {
    // Always add legend items and notes for secondary forms
    dataSources.push(
      {
        positionableType: 'control_measure',
        positionableContext: 'legend_items',
        targetType: 'project_uploads',
        targetId: projectUpload.id,
        order: 2,
        positionableIndexArguments: [projectQuery.data.id, appDivisionContext.appDivisionId],
        sourceModelFormModalProps: {
          isSmall: false,
        },
        sourceModelFormProps: {
          readOnly: projectPermissionContext.readOnly,
        },
        enableSearch: true,
        startInspectionId: inspections[selectedInspectionIndex]
          ? inspections[selectedInspectionIndex].id
          : -1,
        endInspectionId: inspections[selectedInspectionIndex + 1]
          ? inspections[selectedInspectionIndex + 1].id
          : null,
      },
      {
        positionableType: 'pollutant',
        positionableContext: 'legend_items',
        targetType: 'project_uploads',
        targetId: projectUpload.id,
        order: 3,
        positionableIndexArguments: [projectQuery.data.id, appDivisionContext.appDivisionId],
        sourceModelFormModalProps: {
          isSmall: false,
        },
        sourceModelFormProps: {
          readOnly: projectPermissionContext.readOnly,
        },
        enableSearch: true,
        startInspectionId: inspections[selectedInspectionIndex]
          ? inspections[selectedInspectionIndex].id
          : -1,
        endInspectionId: inspections[selectedInspectionIndex + 1]
          ? inspections[selectedInspectionIndex + 1].id
          : null,
      },
      {
        positionableType: 'commentables',
        positionableTypeLabel: 'Notes',
        targetType: 'project_uploads',
        targetId: projectUpload.id,
        positionableIndexArguments: ['project_uploads', projectUpload.id],
        order: 4,
        maxInstances: 1,
        sourceModelFormProps: {
          readOnly: projectPermissionContext.readOnly,
        },
        startInspectionId: inspections[selectedInspectionIndex]
          ? inspections[selectedInspectionIndex].id
          : -1,
        endInspectionId: inspections[selectedInspectionIndex + 1]
          ? inspections[selectedInspectionIndex + 1].id
          : null,
      },
    );

    // Only add findings if secondaryTemplateUploadId is available
    if (secondaryTemplateUploadId) {
      dataSources.push({
        positionableType: 'findings',
        targetType: 'project_inspection_uploads',
        targetId: secondaryTemplateUploadId,
        positionableIndexArguments: (formData = null) => {
          return [
            // either the finding id (for updating) or the inspection id (for creation)
            formData && formData.id ? formData.id : inspections[selectedInspectionIndex].id,
          ];
        },
        order: 1,
        startInspectionId: inspections[selectedInspectionIndex].id,
        endInspectionId: inspections[selectedInspectionIndex + 1]
          ? inspections[selectedInspectionIndex + 1].id
          : null,
        sourceModelFormProps: {
          readOnly:
            projectPermissionContext.readOnly ||
            isInspectionSigned(inspections[selectedInspectionIndex]),
          templateName: get(
            inspections[selectedInspectionIndex],
            'inspection_template.name',
          ),
          enableEditComments: true,
          defaultObservation: client.default_finding_observation,
          inspection_template_type_id: templateId,
        }
      });
    }
  }

  if (secondaryTemplateUploadId && inspectionTemplateData.data?.is_primary) {
    dataSources.push({
      positionableType: 'findings',
      targetType: 'project_inspection_uploads',
      targetId: secondaryTemplateUploadId,
      positionableIndexArguments: (formData = null) => {
        return [
          // either the finding id (for updating) or the inspection id (for creation) 
          formData && formData.id ? formData.id : inspections[selectedInspectionIndex].id,
        ];
      },
      order: 1,
      startInspectionId: inspections[selectedInspectionIndex]
      ? inspections[selectedInspectionIndex].id
      : -1,
    endInspectionId: inspections[selectedInspectionIndex + 1]
      ? inspections[selectedInspectionIndex + 1].id
      : null,
      sourceModelFormProps: {
        readOnly:
          projectPermissionContext.readOnly ||
          isInspectionSigned(inspections[selectedInspectionIndex]),
        templateName: get(
          inspections[selectedInspectionIndex],
          'inspection_template.name',
        ),
        enableEditComments: true,
        defaultObservation: client.default_finding_observation,
        inspection_template_type_id: templateId,
      }
    },
    );
  }

  // Remove commentables, pollutants and control_measure from dataSources if offline
  // This only applies if they were added (i.e., if is_primary is true)
  if (isOffline && inspectionTemplateData.data?.is_primary === true) {
    const filteredDataSources = dataSources.filter((ds) => {
      return ds.positionableType !== 'commentables';
    });

    dataSources.length = 0; // Clear the original array
    filteredDataSources.forEach((ds) => dataSources.push(ds));
  }

  const geturlstring = history.location.search;
  const testUrlStr = geturlstring.match('inspection');

  return (
    <ProjectPermissionContextProvider>
      <MapEditorRouteContext.Provider
        value={{
          dataSources,
          projectName: projectQuery.data.name,
          projectId: projectQuery.data.id,
          mapName: projectUpload.upload.name,
          updatedAt: new Date(
            Date.parse(projectUpload.updatedAt),
          ).toLocaleString(),
          projectTemplate: projectQuery.data.template,

          inspections,
          selectedInspection:
            selectedInspectionIndex === null
              ? null
              : inspections[selectedInspectionIndex],

          setSelectedInspection: (id) => {
            const i = findIndex(inspections, {
              id: toNumber(id),
            });

            if (i > -1) {
              setSelectedInspectionIndex(i);
            }
          },
          goBack: () => {
            const isPublic = window.location.pathname.startsWith("/public")
            const initialInspectionId = inspectId.splice(1, 1)

            history.push(
              (isPublic ? appDivisionContext.getPublicPath(
                testUrlStr === null
                  ? `/projects/${projectId}/active-site-map`
                  : `/inspection/${initialInspectionId}/map`,
              ) : appDivisionContext.getPath(
                testUrlStr === null
                  ? `/projects/${projectId}/active-site-map`
                  : `/inspection/${initialInspectionId}/map`,
              ))
            );
          },
          mapMobileView: mapMobileView,
          setMapMobileView: setMapMobileView,
          showCalibrateMap: showCalibrateMap,
          setShowCalibrateMap: setShowCalibrateMap,
          showRemoved: showRemoved,
          setShowRemoved: setShowRemoved,
          projectUploadId: projectUpload.id,
          projectDocTypeId: projectDocTypeId,
        }}
      >
        <MapEditor
          dataSources={dataSources}
          mapImgSrc={mapImgSrc.current}
          mapId={mapId}
          divisionId={appDivisionContext.appDivisionId}
          projectId={Number(projectId)}
          projectUploadId={projectUpload.id}
          mapName={projectUpload.upload.name}
          mapUploadId={projectUpload.upload.id}
          projectDocTypeId={projectDocTypeId}
          projectUploadOrder={projectUpload.order}
        />
      </MapEditorRouteContext.Provider>
    </ProjectPermissionContextProvider>
  );
}

export default withProjectContext(MapEditorRoute);
